import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Loader2 } from 'lucide-react';
import * as React from 'react';
import { useSearchParams } from 'react-router-dom';

import { WaveArt } from '@/components/illustrations';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  Input,
  Label,
} from '@/components/cargofl-ui';
import { PasswordInput } from '@/components/password-fields';
import { api, ApiError } from '@/lib/api';
import { SESSION_QUERY_KEY, useAuthConfig } from '@/lib/session';
import { useTheme } from '@/lib/theme';

const GOOGLE_CLIENT_ID: string | undefined = import.meta.env.VITE_GOOGLE_CLIENT_ID;
const ALLOWED_DOMAIN = 'innoctive.com';
const GSI_SRC = 'https://accounts.google.com/gsi/client';

/** The exact origin to authorize on the OAuth client - read from the browser rather
 *  than hardcoded, so the message is right on localhost, on pms.cargofl.com, and on
 *  an SWA preview hostname alike. */
const ORIGIN = typeof window === 'undefined' ? '' : window.location.origin;
const IS_LOCAL = /^(localhost|127\.0\.0\.1)$/.test(
  typeof window === 'undefined' ? '' : window.location.hostname,
);

interface GoogleCredentialResponse {
  credential?: string;
}

interface GoogleIdentityServices {
  accounts: {
    id: {
      initialize(config: {
        client_id: string;
        callback: (response: GoogleCredentialResponse) => void;
        auto_select?: boolean;
        /** Restricts the account chooser to one Workspace domain. A UX hint only —
         *  it is trivially bypassed, so the server re-checks the `hd` claim. */
        hd?: string;
      }): void;
      renderButton(
        parent: HTMLElement,
        options: {
          theme?: 'outline' | 'filled_blue' | 'filled_black';
          size?: 'small' | 'medium' | 'large';
          text?: 'signin_with' | 'continue_with';
          shape?: 'rectangular' | 'pill';
          width?: number;
          logo_alignment?: 'left' | 'center';
        },
      ): void;
    };
  };
}

declare global {
  interface Window {
    google?: GoogleIdentityServices;
  }
}

function loadGsiScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.google?.accounts?.id) return resolve();

    const existing = document.querySelector<HTMLScriptElement>(`script[src="${GSI_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('Google script failed')), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = GSI_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Google script failed to load'));
    document.head.appendChild(script);
  });
}

/** What a failed password sign-in says, by status. The API's own text for anything else. */
function passwordSignInError(err: unknown): string {
  if (!(err instanceof ApiError)) return 'Could not reach the server. Check that the API is running.';
  if (err.status === 401) return 'Wrong email or password.';
  if (err.status === 403) return 'This account is no longer active. Contact HR.';
  if (err.status === 429) return 'Too many attempts — wait 15 minutes and try again.';
  return err.message;
}

export function LoginPage() {
  const queryClient = useQueryClient();
  const { resolvedTheme } = useTheme();
  const buttonRef = React.useRef<HTMLDivElement>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  // Google only until GET /auth/config says otherwise, so a default deploy renders exactly
  // as it always has, with no flash of a password form while the config loads.
  const { googleLogin, passwordLogin } = useAuthConfig();
  const [searchParams] = useSearchParams();
  const passwordChanged = searchParams.get('changed') === '1';

  async function submitPassword(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError(null);
    try {
      await api.post('/auth/password', { email: form.get('email'), password: form.get('password') });
      await queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
    } catch (err) {
      setError(passwordSignInError(err));
    } finally {
      setBusy(false);
    }
  }

  const handleCredential = React.useCallback(
    async (response: GoogleCredentialResponse) => {
      if (!response.credential) {
        setError('Google did not return a credential. Please try again.');
        return;
      }
      setBusy(true);
      setError(null);
      try {
        await api.post('/auth/google', { idToken: response.credential });
        // Refetch rather than write the user in by hand: the server decides the
        // role, and this keeps one source of truth for the session shape.
        await queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
      } catch (err) {
        setError(
          err instanceof ApiError
            ? err.message
            : 'Could not reach the server. Check that the API is running.',
        );
      } finally {
        setBusy(false);
      }
    },
    [queryClient],
  );

  React.useEffect(() => {
    if (!GOOGLE_CLIENT_ID || !googleLogin) return;
    let cancelled = false;

    void loadGsiScript()
      .then(() => {
        if (cancelled || !window.google || !buttonRef.current) return;
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: (response) => void handleCredential(response),
          hd: ALLOWED_DOMAIN,
          auto_select: false,
        });
        window.google.accounts.id.renderButton(buttonRef.current, {
          theme: resolvedTheme === 'dark' ? 'filled_black' : 'outline',
          size: 'large',
          text: 'continue_with',
          shape: 'rectangular',
          logo_alignment: 'center',
          width: 320,
        });
      })
      .catch(() => {
        if (!cancelled) setError('Could not load Google sign-in. Check your network and retry.');
      });

    return () => {
      cancelled = true;
    };
  }, [handleCredential, resolvedTheme, googleLogin]);

  return (
    <div className="grid min-h-dvh lg:grid-cols-2">
      {/* Left: the sign-in itself */}
      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-sm space-y-8">
          <div className="flex items-center gap-3">
            <img src="/favicon.png" alt="" className="size-9 rounded-lg" draggable={false} />
            <div className="text-lg font-semibold tracking-[-0.01em]">
              CargoFL <span className="font-normal text-muted-foreground">Performance</span>
            </div>
          </div>

          <WaveArt className="-ml-3 h-20 w-auto" />
          <div className="space-y-2">
            <h1 className="text-2xl font-bold tracking-[-0.02em]">Welcome back</h1>
            <p className="text-sm text-muted-foreground">
              {googleLogin && !passwordLogin
                ? 'Sign in with your work Google account to view your goals, assessments and reviews.'
                : 'Sign in to view your goals, assessments and reviews.'}
            </p>
          </div>

          {passwordChanged ? (
            <Alert>
              <AlertDescription>Password changed. Sign in with your new password.</AlertDescription>
            </Alert>
          ) : null}

          {passwordLogin ? (
            <form className="space-y-4" onSubmit={(e) => void submitPassword(e)}>
              <div className="space-y-1.5">
                <Label htmlFor="login-email">Email</Label>
                <Input id="login-email" name="email" type="email" autoComplete="username" required autoFocus />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="login-password">Password</Label>
                <PasswordInput id="login-password" name="password" autoComplete="current-password" required />
              </div>
              <Button type="submit" size="lg" className="w-full" disabled={busy}>
                {busy ? <Loader2 className="size-4 animate-spin" /> : null}
                Sign in
              </Button>
            </form>
          ) : null}

          {passwordLogin && googleLogin ? (
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <span className="h-px flex-1 bg-border" />
              or
              <span className="h-px flex-1 bg-border" />
            </div>
          ) : null}

          {!googleLogin ? null : GOOGLE_CLIENT_ID ? (
            <div ref={buttonRef} className="flex justify-center" aria-busy={busy} />
          ) : (
            <Alert variant="destructive">
              <AlertTriangle className="size-4" />
              <AlertTitle>Google sign-in is not configured</AlertTitle>
              {/* The fix differs by environment, and showing the dev instructions on
                  the deployed site sends whoever sees it to edit a file that is not
                  there. Vite inlines VITE_ vars at build time, so on a deployed
                  build this needs a new build - not a restart. */}
              <AlertDescription>
                {IS_LOCAL ? (
                  <>
                    Set <code className="font-mono text-xs">VITE_GOOGLE_CLIENT_ID</code> in{' '}
                    <code className="font-mono text-xs">apps/web/.env.local</code>, then restart the
                    dev server. Add <code className="font-mono text-xs">{ORIGIN}</code> as an
                    authorized JavaScript origin on the OAuth client.
                  </>
                ) : (
                  <>
                    This build was made without a Google client ID. Set the{' '}
                    <code className="font-mono text-xs">VITE_GOOGLE_CLIENT_ID</code> repository
                    secret and re-run the deploy workflow — the value is compiled into the bundle,
                    so it needs a rebuild rather than a restart. Add{' '}
                    <code className="font-mono text-xs">{ORIGIN}</code> as an authorized JavaScript
                    origin on the OAuth client.
                  </>
                )}
              </AlertDescription>
            </Alert>
          )}

          {error ? (
            <Alert variant="destructive">
              <AlertTriangle className="size-4" />
              <AlertTitle>Could not sign you in</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}
        </div>
      </div>

      {/* Right: brand panel. A gradient rather than a photo so there is no large
          image to ship or keep licensed, and it renders identically on every box. */}
      <div className="relative hidden overflow-hidden bg-[hsl(160_30%_12%)] lg:block">
        <div
          aria-hidden
          className="absolute inset-0 opacity-90"
          style={{
            background:
              'radial-gradient(120% 90% at 15% 10%, hsl(160 100% 22%) 0%, transparent 55%), radial-gradient(90% 80% at 90% 90%, hsl(190 80% 18%) 0%, transparent 60%)',
          }}
        />
        <div className="relative flex h-full flex-col justify-center gap-8 p-12 text-white">
          <div className="inline-flex w-fit items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider">
            April – March · four quarters
          </div>
          <h2 className="max-w-md text-4xl font-bold leading-[1.1] tracking-[-0.02em]">
            Goals, KRAs and appraisals in one cycle.
          </h2>
          <p className="max-w-md text-sm leading-relaxed text-white/70">
            Organization goals weighted to 100%, quarterly self assessments, and a review chain from
            your manager through HR to management — with every rating traceable to the person who
            gave it.
          </p>
          <Card className="max-w-sm border-white/15 bg-white/10 backdrop-blur">
            <CardContent className="grid grid-cols-3 gap-4 py-5 text-white sm:py-5">
              {[
                { value: '3', label: 'goals minimum' },
                { value: '100%', label: 'total weightage' },
                { value: '5', label: 'review stages' },
              ].map((stat) => (
                <div key={stat.label}>
                  <div className="pms-num text-2xl font-bold">{stat.value}</div>
                  <div className="text-[11px] text-white/60">{stat.label}</div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
